<template>
  <v-container fluid>
    <v-card class="mb-4">
      <v-card-title>
        Reporte de Existencias
      </v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="4">
            <v-text-field
              v-model.trim="filters.q"
              label="Busqueda general"
              hint="SKU, codigo, producto o almacen"
              persistent-hint
              clearable
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.locationId"
              :items="locations"
              item-text="NAME"
              item-value="ID"
              label="Almacen"
              clearable
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.categoryId"
              :items="categories"
              item-text="NAME"
              item-value="ID"
              label="Categoria"
              clearable
              outlined
              dense
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="2">
            <v-text-field v-model.trim="filters.reference" label="SKU" clearable outlined dense />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model.trim="filters.code" label="Codigo de barras" clearable outlined dense />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field v-model.trim="filters.productName" label="Producto" clearable outlined dense />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model.number="filters.stockMin" type="number" step="0.01" label="Existencia min." clearable outlined dense />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model.number="filters.stockMax" type="number" step="0.01" label="Existencia max." clearable outlined dense />
          </v-col>
          <v-col cols="12" md="1">
            <v-select
              v-model="filters.status"
              :items="statusOptions"
              item-text="text"
              item-value="value"
              label="Estado"
              clearable
              outlined
              dense
            />
          </v-col>
        </v-row>

        <div class="d-flex flex-wrap align-center mt-2">
          <v-btn color="primary" class="mr-2 mb-2" :loading="loading" @click="runReport">
            <v-icon left>mdi-play</v-icon>
            Ejecutar
          </v-btn>
          <v-btn text class="mr-2 mb-2" @click="clearFilters">
            <v-icon left>mdi-filter-remove-outline</v-icon>
            Limpiar filtros
          </v-btn>
          <v-btn color="success" class="mb-2" :loading="downloading" :disabled="rows.length === 0" @click="downloadReport">
            <v-icon left>mdi-file-excel</v-icon>
            Descargar Excel
          </v-btn>
          <v-spacer></v-spacer>
          <div class="caption grey--text mb-2 text-right">
            {{ meta.totalCount }} registros
            <br>
            Inversion estimada: ${{ Number(meta.inversionTotal || 0).toFixed(2) }}
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-card>
      <v-card-title>Vista previa</v-card-title>
      <v-data-table
        :headers="headers"
        :items="rows"
        :loading="loading"
        class="elevation-1"
        dense
        :footer-props="{ itemsPerPageText: 'Filas por pagina', itemsPerPageOptions: [10, 25, 50, 100] }"
      >
        <template #[`item.units`]="{ item }">
          <span :class="statusClass(item.status)" class="font-weight-bold">{{ Number(item.units || 0).toFixed(2) }}</span>
        </template>
        <template #[`item.stockSecurity`]="{ item }">
          {{ Number(item.stockSecurity || 0).toFixed(2) }}
        </template>
        <template #[`item.stockMaximum`]="{ item }">
          {{ Number(item.stockMaximum || 0).toFixed(2) }}
        </template>
        <template #[`item.precioCompra`]="{ item }">
          ${{ Number(item.precioCompra || 0).toFixed(2) }}
        </template>
        <template #[`item.status`]="{ item }">
          <v-chip x-small :color="statusColor(item.status)" dark>{{ statusText(item.status) }}</v-chip>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import * as inventoryService from '@/services/inventoryService';
import * as categoryService from '@/services/categoryService';
import * as locationService from '@/services/locationService';

const defaultFilters = () => ({
  q: '',
  locationId: null,
  categoryId: null,
  reference: '',
  code: '',
  productName: '',
  stockMin: null,
  stockMax: null,
  status: null,
});

export default {
  name: 'InventoryExistenceReportView',
  data() {
    return {
      loading: false,
      downloading: false,
      categories: [],
      locations: [],
      rows: [],
      meta: {
        totalCount: 0,
        inversionTotal: 0,
      },
      filters: defaultFilters(),
      statusOptions: [
        { text: 'Todos', value: null },
        { text: 'Bajo minimo', value: 'below_min' },
        { text: 'Normal', value: 'ok' },
        { text: 'Sobre maximo', value: 'above_max' },
      ],
      headers: [
        { text: 'SKU', value: 'reference' },
        { text: 'Codigo de Barras', value: 'code' },
        { text: 'Producto', value: 'name' },
        { text: 'Categoria', value: 'categoryName' },
        { text: 'Almacen', value: 'locationName' },
        { text: 'Stock Min', value: 'stockSecurity', align: 'end' },
        { text: 'Stock Max', value: 'stockMaximum', align: 'end' },
        { text: 'Existencias', value: 'units', align: 'end' },
        { text: 'Precio Compra', value: 'precioCompra', align: 'end' },
        { text: 'Estado', value: 'status', align: 'center' },
      ],
    };
  },
  async mounted() {
    await Promise.all([this.loadCategories(), this.loadLocations()]);
    await this.runReport();
  },
  methods: {
    cleanParams() {
      const params = { ...this.filters };
      Object.keys(params).forEach((key) => {
        if (params[key] === '' || params[key] === undefined) delete params[key];
      });
      return params;
    },
    async loadCategories() {
      try {
        this.categories = await categoryService.fetchCategories();
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al cargar categorias.', type: 'error' });
      }
    },
    async loadLocations() {
      try {
        this.locations = await locationService.fetchLocations();
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al cargar almacenes.', type: 'error' });
      }
    },
    async runReport() {
      this.loading = true;
      try {
        const response = await inventoryService.fetchExistenceReportData(this.cleanParams());
        this.rows = response.rows || [];
        this.meta = response.meta || { totalCount: 0, inversionTotal: 0 };
      } catch (error) {
        this.rows = [];
        this.meta = { totalCount: 0, inversionTotal: 0 };
        this.$emit('show-alert', { message: 'Error al ejecutar reporte de existencias.', type: 'error' });
      } finally {
        this.loading = false;
      }
    },
    clearFilters() {
      this.filters = defaultFilters();
      this.runReport();
    },
    async downloadReport() {
      this.downloading = true;
      try {
        const blob = await inventoryService.downloadExistenceReport(this.cleanParams());
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `reporte_existencias_${new Date().toISOString().slice(0, 10)}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al descargar reporte de existencias.', type: 'error' });
      } finally {
        this.downloading = false;
      }
    },
    statusText(status) {
      if (status === 'below_min') return 'Bajo minimo';
      if (status === 'above_max') return 'Sobre maximo';
      return 'Normal';
    },
    statusColor(status) {
      if (status === 'below_min') return 'red';
      if (status === 'above_max') return 'green';
      return 'blue-grey';
    },
    statusClass(status) {
      if (status === 'below_min') return 'red--text';
      if (status === 'above_max') return 'green--text text--darken-2';
      return '';
    },
  },
};
</script>
